/**
 * Orchestra AI Platform - Main Workflow Builder Component
 * 
 * Main component that combines all workflow builder features
 * 
 * @fileoverview Complete workflow builder with canvas, toolbox, panels, and execution
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactFlowProvider } from 'reactflow';
import { WorkflowCanvas } from './WorkflowCanvas';
import { APIToolbox } from '../Toolbox/APIToolbox';
import { PropertyPanel } from './PropertyPanel';
import { ExecutionPanel } from './ExecutionPanel';
import { TestPanel } from './TestPanel';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useRealtimeCollaboration } from '../../hooks/useRealtimeCollaboration';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { discoverAllCapabilities } from '../../services/a2aDiscoveryService';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import './WorkflowBuilder.css';

/**
 * @typedef {Object} WorkflowBuilderProps
 * @property {string} workflowId - Workflow ID
 * @property {boolean} [readOnly] - Whether builder is read-only
 */

/**
 * Main Workflow Builder Component
 * @param {WorkflowBuilderProps} props
 */
export function WorkflowBuilder({ workflowId, readOnly = false }) {
  const [showToolbox, setShowToolbox] = useState(true);
  const [showPropertyPanel, setShowPropertyPanel] = useState(false);
  const [showExecutionPanel, setShowExecutionPanel] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [apis, setAPIs] = useState([]);
  const [apisLoading, setAPIsLoading] = useState(true);
  const [apisError, setAPIsError] = useState(null);

  const { nodes, edges, undo, redo, deleteNode, selectedNodeId, reset } = useWorkflowStore();

  // Auto-save
  useAutoSave(workflowId, nodes, edges);

  // Real-time collaboration
  useRealtimeCollaboration(workflowId);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: 's', ctrl: true, action: handleSave, description: 'Save workflow' },
    { key: 'z', ctrl: true, shift: false, action: undo, description: 'Undo' },
    { key: 'z', ctrl: true, shift: true, action: redo, description: 'Redo' },
    { key: 'Delete', action: handleDelete, description: 'Delete selected' },
    { key: 'Backspace', action: handleDelete, description: 'Delete selected' },
    { key: 'c', ctrl: true, action: handleCopy, description: 'Copy' },
    { key: 'v', ctrl: true, action: handlePaste, description: 'Paste' },
    { key: 'a', ctrl: true, action: handleSelectAll, description: 'Select all' },
    { key: 'd', ctrl: true, action: handleDuplicate, description: 'Duplicate' },
  ], !readOnly);

  // Load APIs on mount
  useEffect(() => {
    loadAPIs();
  }, []);

  /**
   * Load available APIs from all A2A servers
   */
  const loadAPIs = async () => {
    try {
      setAPIsLoading(true);
      setAPIsError(null);
      
      console.log('[Workflow Builder] Discovering APIs from all A2A servers...');
      const discoveredAPIs = await discoverAllCapabilities();
      
      if (discoveredAPIs.length === 0) {
        setAPIsError('No APIs discovered. Please check server connections.');
        toast.error('No APIs found. Check server connections.');
      } else {
        setAPIs(discoveredAPIs);
        toast.success(`Discovered ${discoveredAPIs.length} APIs`);
        console.log(`[Workflow Builder] Loaded ${discoveredAPIs.length} APIs`);
      }
    } catch (err) {
      console.error('[Workflow Builder] Failed to load APIs:', err);
      setAPIsError(err.message || 'Failed to discover APIs');
      toast.error('Failed to discover APIs: ' + (err.message || 'Unknown error'));
    } finally {
      setAPIsLoading(false);
    }
  };

  /**
   * Handle save
   */
  async function handleSave() {
    try {
      const { error } = await supabase
        .from('developer_workflows')
        .update({
          workflow_data: {
            nodes,
            edges,
            viewport: { x: 0, y: 0, zoom: 1 },
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', workflowId);

      if (error) throw error;
      toast.success('Workflow saved');
    } catch (error) {
      toast.error('Failed to save workflow: ' + error.message);
    }
  }

  /**
   * Handle delete selected nodes
   */
  function handleDelete() {
    if (selectedNodeId) {
      deleteNode(selectedNodeId);
      toast.success('Node deleted');
    }
  }

  /**
   * Handle copy
   */
  function handleCopy() {
    // TODO: Implement copy functionality
    toast('Copy functionality coming soon', { icon: 'ℹ️' });
  }

  /**
   * Handle paste
   */
  function handlePaste() {
    // TODO: Implement paste functionality
    toast('Paste functionality coming soon', { icon: 'ℹ️' });
  }

  /**
   * Handle select all
   */
  function handleSelectAll() {
    // TODO: Implement select all
    toast('Select all functionality coming soon', { icon: 'ℹ️' });
  }

  /**
   * Handle duplicate
   */
  function handleDuplicate() {
    // TODO: Implement duplicate
    toast('Duplicate functionality coming soon', { icon: 'ℹ️' });
  }

  return (
    <ReactFlowProvider>
      <div className="workflow-builder">
        {/* Header */}
        <div className="builder-header">
          <h1>Workflow Builder</h1>
          <div className="builder-actions">
            <button
              onClick={() => setShowToolbox(!showToolbox)}
              className="builder-button"
            >
              {showToolbox ? 'Hide' : 'Show'} Toolbox
            </button>
            <button
              onClick={() => setShowPropertyPanel(!showPropertyPanel)}
              className="builder-button"
            >
              {showPropertyPanel ? 'Hide' : 'Show'} Properties
            </button>
            <button
              onClick={() => {
                setShowExecutionPanel(!showExecutionPanel);
                setShowTestPanel(false);
              }}
              className="builder-button"
            >
              {showExecutionPanel ? 'Hide' : 'Show'} Execution
            </button>
            <button
              onClick={() => {
                setShowTestPanel(!showTestPanel);
                setShowExecutionPanel(false);
              }}
              className="builder-button"
            >
              {showTestPanel ? 'Hide' : 'Show'} Test
            </button>
            {!readOnly && (
              <button
                onClick={handleSave}
                className="builder-button primary"
              >
                Save (Ctrl+S)
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="builder-main">
          {/* Toolbox */}
          {showToolbox && !readOnly && (
            <div className="builder-toolbox">
              {apisLoading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '20px'
                }}>
                  <div style={{ fontSize: '48px' }}>⏳</div>
                  <p style={{ color: '#6b7280' }}>Discovering APIs from A2A servers...</p>
                </div>
              ) : apisError ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '20px'
                }}>
                  <div style={{ fontSize: '48px' }}>⚠️</div>
                  <p style={{ color: '#ef4444', marginBottom: '16px' }}>{apisError}</p>
                  <button
                    onClick={loadAPIs}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <APIToolbox 
                  apis={apis} 
                  onAPISelect={(api, action) => {
                    console.log('API selected in workflow builder:', api, action);
                    // TODO: Handle API selection (e.g., add to canvas)
                    if (action === 'test') {
                      toast('Drag API to canvas to add it to workflow', { icon: '💡' });
                    }
                  }}
                />
              )}
            </div>
          )}

          {/* Canvas */}
          <div className="builder-canvas-container">
            <WorkflowCanvas workflowId={workflowId} readOnly={readOnly} onSave={handleSave} />
          </div>

          {/* Property Panel */}
          {showPropertyPanel && (
            <div className="builder-property-panel">
              <PropertyPanel onClose={() => setShowPropertyPanel(false)} />
            </div>
          )}
        </div>

        {/* Bottom Panels */}
        {(showExecutionPanel || showTestPanel) && (
          <div className="builder-bottom-panel">
            {showExecutionPanel && <ExecutionPanel workflowId={workflowId} />}
            {showTestPanel && <TestPanel workflowId={workflowId} />}
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}

WorkflowBuilder.propTypes = {
  workflowId: PropTypes.string,
  readOnly: PropTypes.bool,
};

